import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import * as yup from "yup";
import { Row, Col, Image, Card, Form, Button, Modal } from "react-bootstrap";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { authHandler } from "../redux/handlers";

const STEPS = {
  EMAIL_FORM: 1,
  EMAIL_SENT: 2,
};

const TIMER_DURATION = 60;

const emailSchema = yup.object().shape({
  email: yup
    .string()
    .required("กรุณากรอกอีเมล")
    .email("รูปแบบอีเมลไม่ถูกต้อง"),
});

const SuccessModal = ({ show, onHide, message }) => (
  <Modal show={show} onHide={onHide} size="sm" centered>
    <Modal.Body>
      <div className="px-3 py-4">
        <div className="flex flex-col items-center text-center mb-4">
          <svg width="84" height="83" viewBox="0 0 84 83" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="42" cy="41.5" r="41.5" fill="#2DC76D" />
            <path d="M56.0123 30.9902L36.7444 50.2581L27.9863 41.5" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="text-center fs-22 tc-7">{message}</div>
      </div>
    </Modal.Body>
  </Modal>
);

const useCountdownTimer = (initialTime = TIMER_DURATION) => {
  const [timer, setTimer] = useState(initialTime);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!timer || !isActive) {
      setIsActive(false);
      return;
    }
    const intervalId = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timer, isActive]);

  const resetTimer = () => {
    setTimer(initialTime);
    setIsActive(true);
  };

  return { timer, isTimerActive: isActive, resetTimer };
};

export function ForgotPassword() {
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEPS.EMAIL_FORM);
  const [email, setEmail] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 2000);
  };

  const handleSendResetEmail = async (emailAddress) => {
    await dispatch(authHandler.requestPasswordReset(emailAddress));
    setEmail(emailAddress);
    showSuccess("ส่งอีเมลรีเซ็ตรหัสผ่านสำเร็จ");
    setTimeout(() => setStep(STEPS.EMAIL_SENT), 2000);
  };

  const handleResendEmail = async () => {
    await dispatch(authHandler.requestPasswordReset(email));
    showSuccess("ส่งอีเมลซ้ำสำเร็จ");
  };

  return (
    <div className="m-0 w-100 p-0 bg-white">
      <Row className="g-0 h-100vh">
        <Col xl={5} lg={6} className="d-none d-lg-block">
          <Image src="/images/cover_login.png" className="img-fit" />
        </Col>
        <Col xl={7} lg={6} className="d-flex flex-column py-4">
          <div className="mb-auto"></div>
          <Card className="card-login-st-1 mb-auto h-auto">
            <Card.Body>
              <div className="text-center">
                <Image src="/logo.png" width={150} className="mb-4 mx-auto" />
              </div>
              <div className="text-center text-[#FF731D] font-bold text-[24px] fw-5 mb-4">
                ADMIN SYSTEM
              </div>

              {step === STEPS.EMAIL_FORM ? (
                <EmailForm onSubmit={handleSendResetEmail} />
              ) : (
                <EmailSentSuccess
                  email={email}
                  onResend={handleResendEmail}
                />
              )}
            </Card.Body>
          </Card>
          <div className="text-center fs-20 tc-3 d-flex align-items-end pt-4 justify-content-center">
            <span className="me-3">Power By</span>
            <div className="pb-05">
              <Image src="/images/logo_allder.png" width={98} />
            </div>
          </div>
        </Col>
      </Row>

      <SuccessModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
}

const EmailForm = ({ onSubmit }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      await onSubmit(values.email);
    } catch (error) {
      console.error("Error sending email:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="text-center fw-5 fs-20 mb-3">ลืมรหัสผ่าน</div>
      <div className="text-center tc-3 mb-4">
        กรอกอีเมลของคุณเพื่อรับลิงก์ตั้งรหัสผ่านใหม่
      </div>
      <Formik
        initialValues={{ email: "" }}
        validationSchema={emailSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, handleChange, handleBlur, touched, errors, values }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Control
              type="email"
              name="email"
              placeholder="กรอกอีเมล เช่น admin@example.com"
              onChange={handleChange}
              onBlur={handleBlur}
              isValid={touched.email && !errors.email && values.email}
              isInvalid={touched.email && !!errors.email}
              className="form-st-1 mb-2"
            />
            {touched.email && errors.email && (
              <div className="text-danger small mb-3">{errors.email}</div>
            )}
            <div className="d-flex gap-2 mt-4">
              <Link to="/login" className="btn btn-st-2 w-50">
                ยกเลิก
              </Link>
              <Button
                type="submit"
                className="btn-login w-50"
                disabled={!values.email || !!errors.email || isLoading}
              >
                {isLoading ? "กำลังส่ง..." : "ถัดไป"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

const EmailSentSuccess = ({ email, onResend }) => {
  const { timer, isTimerActive, resetTimer } = useCountdownTimer();

  const handleResend = async () => {
    try {
      await onResend();
      resetTimer();
    } catch (error) {
      console.error("Failed to resend:", error);
    }
  };

  return (
    <div className="text-center">
      <div className="mx-auto w-[72px] h-[72px] mb-4 rounded-full bg-[#2DC76D] d-flex align-items-center justify-content-center" style={{ width: 72, height: 72, borderRadius: "50%", background: "#2DC76D", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <EmailOutlinedIcon className="text-white" style={{ fontSize: 32, color: "white" }} />
      </div>
      <div className="fw-bold fs-20 mb-3">คำขอตั้งรหัสผ่านใหม่สำเร็จ</div>
      <div className="tc-3 mb-4">
        เราได้ส่งอีเมลยืนยันไปที่ <span className="fw-bold">{email}</span>
        <br />
        เปิดอีเมลและตั้งรหัสผ่านใหม่ของคุณ (ลิงก์มีอายุ 24 ชั่วโมง)
      </div>
      <Button
        className="btn-login w-100 mb-3"
        onClick={handleResend}
        disabled={isTimerActive}
      >
        ไม่ได้รับอีเมล ? คลิกที่นี่เพื่อส่งอีกครั้ง
        {isTimerActive && ` (${timer})`}
      </Button>
      <Link to="/login" className="alink-st-1">
        กลับหน้าเข้าสู่ระบบ
      </Link>
    </div>
  );
};
